import React, { useCallback } from 'react';
import { createGroup } from 'api/chat/group/create-group';
import { getSuggestUser } from 'api/chat/user/get-suggest-user';
import { FormikProps, FormWrapper, Yup } from 'components/form';
import { Button } from 'components/parts';
import { Form } from 'formik';
import { useSubscription } from 'hooks/use-subscription.hook';
import useSearchListUser from 'hooks/useSearchListUser';
import { cloneDeep } from 'lodash';
import { ContactType } from 'pages/chat/consts';
import { useChatContext } from 'pages/chat/context';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { IModal } from 'services/modal';
import { ReactComponent as BlueCheckBox } from 'asserts/icons/check-circle-green-hover.svg';
import { ReactComponent as RegularCheckBox } from 'asserts/icons/check-circle-primary-36.svg';
import { ReactComponent as CloseIcon } from 'asserts/icons/close-primary-60.svg';
import DebounceSearch from '../DebounceSearch';
import SkeletonThumbnail from '../Skeleton/SkeletonThumbnail';
import { useSession } from 'hooks/use-session.hook';
import { getUserObject } from 'api/chat/user/get-user-object';

interface IFormCreateGroup {
  group_name: string;
  search_user: string;
  users: Array<string>;
}

interface IUser {
  _id: string;
  user_id: number;
  emp_name: string;
  path_avatar: string;
}

const getValidationSchema = (t) => {
  return Yup.object({
    group_name: Yup.string()
      .required(t('MS01', { field: 'Tên nhóm' }))
      .min(1, t('MS02', { field: 'Tên nhóm', min: 1 }))
      .max(500, t('MS03', { field: 'Tên nhóm', max: 500 })),
  });
};

const RenderAvatar = ({ pathAvatar }: { pathAvatar: string }) => {
  const avatar =
    pathAvatar ||
    'https://png.pngtree.com/png-vector/20191101/ourmid/pngtree-cartoon-color-simple-male-avatar-png-image_1934459.jpg';

  return (
    <div className='item-single-avatar'>
      <img src={avatar} alt='user icon' />
    </div>
  );
};

const UserItem = ({
  user,
  onSelectedItem,
  selectedUsers,
}: {
  user: IUser;
  onSelectedItem: (user: IUser, selected: boolean) => void;
  selectedUsers: IUser[];
}) => {
  const [isSelect, setIsSelect] = useState<boolean>(false);
  const onClickItem = () => {
    setIsSelect(!isSelect);
    onSelectedItem(user, !isSelect);
  };

  useEffect(() => {
    if (selectedUsers.find((item) => item._id === user._id)) {
      setIsSelect(true);
    } else {
      setIsSelect(false);
    }
  }, [selectedUsers, user._id]);

  return (
    <div className='user-item' onClick={onClickItem}>
      <div className='user-item__left'>
        <RenderAvatar pathAvatar={user.path_avatar} />
        <div className='name'>{user.emp_name}</div>
      </div>

      <div className='user-item__right'>
        {isSelect ? <BlueCheckBox className='checkbox-icon' /> : <RegularCheckBox className='checkbox-icon' />}
      </div>
    </div>
  );
};

const SelectedUserItem = ({
  user,
  onSelectedItem,
}: {
  user: IUser;
  onSelectedItem: (user: IUser, selected: boolean) => void;
}) => {
  const onClickItem = () => {
    onSelectedItem(user, false);
  };

  return (
    <div className='selected-user-item' onClick={onClickItem}>
      <RenderAvatar pathAvatar={user.path_avatar} />
      <CloseIcon className='icon-close' />
    </div>
  );
};

function CreateGroupModal(props: IModal) {
  const {
    modalRef,
    data: { type, title, _id, chat_name },
  } = props;

  console.log(type);

  // const [users, setUsers] = useState<IUser[]>([]);
  const formikRef = useRef<FormikProps<Partial<IFormCreateGroup>>>(null);
  const { t } = useTranslation();
  const [keySearch, setKeySearch] = useState<string>('');
  const [listUser, loading, setLoading] = useSearchListUser(keySearch);
  const [userObject, setUserObject] = useState<IUser>(null);
  const chatContext = useChatContext();
  const { updateNameOfConversation } = chatContext;

  const [suggestUsers, setSuggestUsers] = useState<IUser[]>([]);
  const subscription = useSubscription();
  const navigate = useNavigate();
  const [selectedUsers, setSelectedUsers] = useState<IUser[] | null>([]);
  const { userInfo } = useSession();

  const LIMIT_SUGGEST_USERS = 10;
  const initialValues = useMemo(
    () => ({
      group_name: chat_name,
      users: [],
    }),
    [chat_name]
  );

  useEffect(() => {
    if (!userInfo?.userId) return;

    getUserObject(userInfo.userId).subscribe({
      next: (getUserObjectRes) => {
        setUserObject(getUserObjectRes.data);
      },
    });
  }, [userInfo?.userId]);

  useEffect(() => {
    setLoading(true);
    const getSuggestSub = getSuggestUser(LIMIT_SUGGEST_USERS).subscribe({
      next: (res) => {
        setSuggestUsers(res.data);
        setLoading(false);
      },
      complete: () => {
        setLoading(false);
      },
    });

    subscription.add(getSuggestSub);
  }, [setLoading, subscription]);

  // const getSelected = (selected: Array<string>) => {
  //   return users.filter((item) => selected.includes(`${item._id}`)) || [];
  // };

  const onSubmit = (values: IFormCreateGroup) => {
    let group_name;

    if (type === 'add') {
      group_name = [...selectedUsers, userObject].reduce((acc: string, next: IUser): string => {
        acc = acc + (acc ? `,${next.emp_name}` : next.emp_name);

        return acc;
      }, '' as string);
    } else {
      group_name = values.group_name;
    }

    const userIds = [...selectedUsers].map((user) => user._id);
    const createGroupSub = createGroup(group_name, userIds, _id).subscribe((res) => {
      if (res.data) {
        modalRef.close();
        chatContext.setContactType(ContactType.CONVERSATION);
        chatContext.getConversations();
        type === 'edit' && updateNameOfConversation(_id, group_name);
        navigate(`/chat/${res.data}`);
      }
    });

    subscription.add(createGroupSub);
  };

  const handleChange = (value: string) => {
    type === 'add' && setKeySearch(value);
  };

  const handleSelectedItem = (user: IUser, selected: boolean) => {
    if (selected) {
      setSelectedUsers((selectedUsers) => [...selectedUsers, user]);
    } else {
      let cloneUserList = cloneDeep(selectedUsers);

      cloneUserList = cloneUserList.filter((item) => item._id !== user._id);

      setSelectedUsers(cloneUserList);
    }
  };

  const renderListUser = () => {
    const datasource = keySearch ? listUser : suggestUsers;

    return datasource?.length ? (
      datasource?.map((item) => (
        <UserItem selectedUsers={selectedUsers} key={item._id} user={item} onSelectedItem={handleSelectedItem} />
      ))
    ) : (
      <h4>Không có kết quả phù hợp được tìm thấy</h4>
    );
  };

  const isDisableSubmit = useCallback(
    (isSubmitting, errors, dirty) =>
      (type === 'edit' && ((Object.keys(errors).length > 0 && dirty) || !dirty)) ||
      isSubmitting ||
      (type === 'add' && selectedUsers.length < 2),
    [selectedUsers.length, type]
  );

  return (
    <FormWrapper<Partial<IFormCreateGroup>>
      initialValues={initialValues}
      onSubmit={onSubmit}
      validationSchema={type === 'edit' ? getValidationSchema(t) : null}
      innerRef={formikRef}
      enableReinitialize
      isRenderOuter
    >
      {({ errors, isSubmitting, dirty }) => {
        return (
          <Form>
            <div>
              <div className='modal-content'>
                <div className='modal-header'>
                  <div className='modal-title h2'>{title}</div>
                </div>
                <div className='modal-body'>
                  <div className='form-group row'>
                    <label className='col-sm-3 col-form-label'>
                      {type === 'add' ? 'Tìm kiếm user' : 'Chinh sua ten'}
                    </label>
                    <div className='col-sm-7'>
                      <DebounceSearch
                        showError={true}
                        type='text'
                        name={type === 'add' ? 'search_user' : 'group_name'}
                        onSearch={handleChange}
                      />
                    </div>
                  </div>

                  {type === 'add' && (
                    <>
                      <div className='form-group row list-selected-user'>
                        {selectedUsers?.map((user) => (
                          <>
                            <SelectedUserItem user={user} key={user._id} onSelectedItem={handleSelectedItem} />
                          </>
                        ))}
                      </div>

                      <div className='list-user'>
                        <h5>{keySearch ? 'Kết quả tìm kiếm' : 'Được gợi ý'}</h5>
                        {!loading ? (
                          <div className={`users ${!listUser?.length && keySearch ? 'not-result' : ''}`}>
                            {renderListUser()}
                          </div>
                        ) : (
                          <SkeletonThumbnail />
                        )}
                      </div>
                    </>
                  )}
                </div>
                <div className='modal-footer'>
                  <Button type='button' theme='danger' className='h-mr-8' onClick={modalRef.close}>
                    {t('common.btn.cancel')}
                  </Button>
                  <Button type='submit' disabled={isDisableSubmit(isSubmitting, errors, dirty)}>
                    {t('common.btn.confirm')}
                  </Button>
                </div>
              </div>
            </div>
          </Form>
        );
      }}
    </FormWrapper>
  );
}

export default CreateGroupModal;
